import React, { FunctionComponent, Fragment, useState, useRef } from "react";
import styles from "./BlogPostCover.module.scss";
import BlogPostCoverProps from "./BlogPostCoverProps.interface";

import useHover from "./hooks/useHover";
import useInterval from "./hooks/useInterval";

import { format } from "date-fns";
import { useSelector } from "react-redux";
import BlogPostPageProps from "pages/BlogPostPage/BlogPostPageProps.interface";
import combineClasses from "helpers/combine-classes";
import DynamicHeading from "components/DynamicHeading/DynamicHeading";
import getWrapperClasses from "blocks/helpers/getWrapperClasses";
import IconViewer from "components/IconViewer/IconViewer";
import ImageViewer from "components/ImageViewer/ImageViewer";
import useWrapperStyles from "blocks/hooks/useWrapperStyles";
import useSplitBackground from "blocks/hooks/useSplitBackground";
import useAnchor from "blocks/hooks/useAnchor";

const BlogPostCover: FunctionComponent<BlogPostCoverProps> = (props) => {
    const [currentSlide, setCurrentSlide] = useState(0);

    const identifier = useAnchor(props.settings.anchorMenuLabel);
    const { splitClasses, splitStyles } = useSplitBackground(props);

    const blogPage = useSelector(state => state.page.content as BlogPostPageProps);
    const wrapperClasses = getWrapperClasses(props.settings);
    const wrapperStyles = useWrapperStyles(props.settings);
    const images = props.content.media;
    const delay = 3000;

    const wrapperStylesElementRef = useRef<any>(null);
    const isHover = useHover(wrapperStylesElementRef);

    useInterval(() => {
        handleClickRight();
    }, !isHover ? delay : null);

    const handleClickLeft = () => {
        if (currentSlide === 0) {
            setCurrentSlide(images.length - 1);
        }
        else {
            setCurrentSlide(currentSlide - 1);
        }
    };

    const handleClickRight = () => {
        if (currentSlide === images.length - 1) {
            setCurrentSlide(0);
        }
        else {
            setCurrentSlide(currentSlide + 1);
        }
    };

    const handlePaginationClick = (index: number) => {
        setCurrentSlide(index);
    };

    return (
        <section className={combineClasses(styles.wrapper, ...wrapperClasses)} ref={wrapperStylesElementRef} style={{ ...wrapperStyles, ...splitStyles }} id={identifier}>
            {/* Media */}
            <div className={combineClasses(styles.slider, ...splitClasses, !blogPage.heading ? styles.no_post : undefined)}>
                {/* Images */}
                {images && images.length > 0 && images.map((image, index) => {
                    return (
                        <ImageViewer key={index} className={combineClasses(styles.image, currentSlide === index ? styles.is_active : styles.is_inactive)} src={image.umbracoFile.src} type="cover" focalPoint={image.umbracoFile.focalPoint} alt={blogPage.heading} />
                    );
                })}

                {/* Pagination */}
                {images && images.length > 1 ? (
                    <div className={styles.pagination}>
                        {images && images.map((_, index) => {
                            const currentImageClass = currentSlide === index ? styles.is_active : styles.is_inactive;
                            return (
                                <button key={index} className={styles.pagination_item}>
                                    <span className={combineClasses(styles.pagination_dot, currentImageClass)} onClick={() => handlePaginationClick(index)}></span>
                                </button>
                            );
                        })}
                    </div>
                ) : null}

                {/* Controls */}
                {images && images.length > 1 ? (
                    <div className={styles.controls}>
                        <button className={styles.control_button} onClick={() => handleClickLeft()}>
                            <IconViewer className={styles.control_icon} name="chevron-circle-left" />
                        </button>
                        <button className={styles.control_button} onClick={() => handleClickRight()}>
                            <IconViewer className={styles.control_icon} name="chevron-circle-right" />
                        </button>
                    </div>
                ) : null}
            </div>

            {/* Content */}
            <div className={combineClasses(styles.content, !blogPage.heading ? styles.no_post : undefined)}>
                <div>
                    {blogPage.heading ? (
                        <DynamicHeading className={styles.heading} headlineSize={"H3"} headlineWeight={"H1"}>{blogPage.heading}</DynamicHeading>
                    ) : null}

                    <div className={styles.tagline}>
                        {blogPage.datePublished ? (
                            <Fragment>
                                <span>{format(new Date(blogPage.datePublished), "d, MMMM, yyyy")}</span>
                                <span className={styles.spacer}>&bull;</span>
                            </Fragment>
                        ) : null}

                        {blogPage.author ? (
                            <span>{blogPage.author}</span>
                        ) : null}
                    </div>
                </div>
            </div>
        </section>
    );
};

export default BlogPostCover;
